import { getEntitySeedSize } from "@/config/reference.config.js";
import { sdk, httpGet } from "@/lib/api/sdk-instance.js";
import {
  getTenantRolePermissionSnapshot,
  tenantSnapshotHasAnyPagePermission,
} from "@/lib/tenant-role-permissions.js";
import { useAuthStore } from "@/store/auth.store.js";
import { useTenantStore } from "@/store/tenant.store.js";
import { useReferenceStore } from "@/store/reference.store.js";

const EMPTY = () => ({ roles: [], units: [], employees: [] });

const EMAIL_LIKE_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function rowToEmployee(item) {
  const value = item?.value?._id ?? item?.value ?? item?._id ?? item?.id;
  const fullName = [item?.firstName, item?.lastName].filter(Boolean).join(" ").trim();
  const rawLabel = typeof item?.label === "string" ? item.label.trim() : "";
  const name = typeof item?.name === "string" ? item.name.trim() : "";
  const email = typeof item?.email === "string" ? item.email.trim() : "";
  const label =
    fullName
    || (rawLabel && !EMAIL_LIKE_RE.test(rawLabel) ? rawLabel : "")
    || (name && !EMAIL_LIKE_RE.test(name) ? name : "")
    || rawLabel
    || name
    || email
    || item?.userName
    || String(value ?? "");
  return { value, label };
}

/**
 * Prefer cached reference rows (bulk + hybrid seed), same source as ReferenceLabel / hydration.
 */
function mergeUsersFromReferenceStore() {
  const { seedData, entities } = useReferenceStore.getState();
  const seed = seedData?.user;
  const bulk = entities?.user;
  const merged = [];
  const seen = new Set();

  const absorb = (list) => {
    if (!Array.isArray(list)) return;
    for (const item of list) {
      const row = rowToEmployee(item);
      const id = row.value != null ? String(row.value) : "";
      if (!id || seen.has(id)) continue;
      seen.add(id);
      merged.push(row);
    }
  };

  absorb(bulk);
  absorb(seed);
  return merged;
}

/**
 * Assignee / employee pick-lists (BCM, kriz, tatbikat). Tek kullanıcı kaynağı: reference-data `user`
 * (+ opsiyonel GET /user fallback yalnızca kullanıcı yönetimi yetkisi varken — aksi halde 403 / gereksiz trafik).
 */
export async function fetchEmployeeOptions() {
  let employees = mergeUsersFromReferenceStore();
  if (employees.length > 0) {
    return { ...EMPTY(), employees };
  }

  try {
    const limit = getEntitySeedSize("user");
    const res = await sdk.referenceData.seed({ entity: "user", limit });
    const rows = res?.data;
    const version = res?.version;
    if (Array.isArray(rows) && rows.length > 0) {
      employees = rows.map((item) => rowToEmployee(item));
      useReferenceStore.getState().setSeedData("user", rows, version);
      return { ...EMPTY(), employees };
    }
  } catch {
    /* same contract as global reference hydration */
  }

  const authUser = useAuthStore.getState().user;
  const tenantId = useTenantStore.getState().selectedTenantId;
  const snapshot = getTenantRolePermissionSnapshot(authUser, tenantId);
  if (
    !tenantSnapshotHasAnyPagePermission(snapshot, "userOperationsUserManagement")
  ) {
    return EMPTY();
  }

  try {
    const res = await httpGet("/user", { pageSize: 1000, page: 1 });
    const envelope = res?.data ?? res;
    const items =
      envelope?.items ??
      envelope?.data?.items ??
      (Array.isArray(envelope?.data) ? envelope.data : []) ??
      [];
    const list = Array.isArray(items) ? items : [];
    employees = list.map((u) => ({
      value: u._id || u.id,
      label:
        (u.firstName || u.lastName)
          ? `${u.firstName || ""} ${u.lastName || ""}`.trim()
          : (u.name || u.userName || u.email || ""),
    }));
    return { ...EMPTY(), employees };
  } catch {
    return EMPTY();
  }
}
